/**
 * The BowlineConnectForm component
 */
import { getProjectAttributes } from "@bkry/bowline-redux/projects";
import { getWalletAttributes } from "@bkry/bowline-redux/wallets";
import { getEnv } from "@bkry/bowline-utils";
import { useWeb3React } from "@web3-react/core";
import PropTypes from "prop-types";
import React, { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { Button, Form, FormGroup, Input, Label } from "reactstrap";

/**
 * Stateless react component to render the BowlineConnectForm component
 * @param {Object} props the props passed to the component.
 */
const BowlineConnectForm = ({ onSubmit }, context) => {
  const REACT_APP_PROJECT_ID = getEnv("REACT_APP_PROJECT_ID");
  const { connector } = useWeb3React();

  const {
    handleSubmit,
    control,
    setValue,
    watch,
    register,
    formState: { errors, isValid },
  } = useForm();

  const projectData = useSelector((state) =>
    getProjectAttributes(state, REACT_APP_PROJECT_ID)
  );

  const projectConstants = useSelector((state) => state.constants.project);

  const currentWalletId = useSelector((state) => state.wallets.currentWalletId);

  const currentWalletData = useSelector((state) =>
    getWalletAttributes(state, currentWalletId)
  );

  const isDisabled =
    projectData?.bowline_connect_mode ===
    projectConstants.bowlineConnectMode.disabled;

  const isOptional =
    projectData?.bowline_connect_mode ===
    projectConstants.bowlineConnectMode.optional;

  const isRequired =
    projectData?.bowline_connect_mode ===
    projectConstants.bowlineConnectMode.required;

  const isDoubleOptIn =
    projectData?.bowline_connect_mode ===
    projectConstants.bowlineConnectMode.doubleOptIn;

  useEffect(() => {
    if (isRequired || isDoubleOptIn) {
      setValue("userSharesEmail", true);
    }
  }, [isRequired, isDoubleOptIn]);

  useEffect(() => {
    if (connector?.userInfo) {
      setValue("email", connector?.userInfo?.email);
    }
  }, [connector?.userInfo]);

  if (isDisabled) {
    return (
      <Button color="primary" className="w-50 m-auto" onClick={onSubmit}>
        {context.t("Continue")}
      </Button>
    );
  }

  return (
    <Form onSubmit={handleSubmit(onSubmit)}>
      <h2>{context.t("Settings")}</h2>
      {isOptional ? (
        <>
          <p className="mt-4">
            {context.t("{project} asks for your email address", {
              project: projectData?.title,
            })}
          </p>

          <FormGroup check>
            <Input
              checked={!watch("userSharesEmail")}
              type="radio"
              {...register("userSharesEmail")}
              onChange={(evt) =>
                setValue("userSharesEmail", !evt.target.checked, {
                  shouldValidate: true,
                })
              }
              id="dont_share_email"
            />

            <Label for="dont_share_email">
              {context.t("Don't share email")}
            </Label>
          </FormGroup>

          <FormGroup check>
            <Input
              checked={watch("userSharesEmail")}
              type="radio"
              {...register("userSharesEmail")}
              onChange={(evt) =>
                setValue("userSharesEmail", evt.target.checked, {
                  shouldValidate: true,
                })
              }
              id="share_email"
            />

            <Label for="share_email">
              {context.t("Receive notifications from this project")}
            </Label>
          </FormGroup>
        </>
      ) : (
        <>
          <p className="mt-4">
            {context.t("{project} requires your email address to continue", {
              project: projectData?.title,
            })}
          </p>
          <FormGroup tag="fieldset">
            <p className="small text-muted">
              {context.t(
                "{project} will collect your email address and utilize it in accordance with their data protection policy. Bowline will also store your email address for the purpose of sending notifications.",
                {
                  project: projectData?.title,
                }
              )}
            </p>
          </FormGroup>
        </>
      )}

      {currentWalletData.email_masked && (
        <div className="mb-3">
          <p>{context.t("Your stored email")}</p>
          <p>{currentWalletData.email_masked}</p>
        </div>
      )}

      {watch("userSharesEmail") && !currentWalletData.email_masked && (
        <>
          <div className="mb-3">
            <FormGroup floating>
              <Controller
                render={({ field }) => (
                  <Input
                    {...field}
                    onChange={(v) => {
                      field.onChange(v);
                    }}
                    type="email"
                    placeholder={context.t("Email address")}
                    id="email"
                    invalid={errors?.email ? true : false}
                  />
                )}
                name="email"
                control={control}
                rules={{ required: true }}
              />
              <Label for="email">{context.t("Email address")}</Label>

              {errors.email?.type === "required" && (
                <p className="text-danger small">
                  {context.t("Email is required")}
                </p>
              )}
            </FormGroup>
          </div>

          {!isOptional && (
            <Label className="mt-3">
              <Input
                checked={watch("isSubscriptionAccepted")}
                type="checkbox"
                invalid={errors.isSubscriptionAccepted ? true : false}
                {...register("isSubscriptionAccepted", { required: true })}
                onChange={(evt) =>
                  setValue("isSubscriptionAccepted", evt.target.checked, {
                    shouldValidate: true,
                  })
                }
              />
              <span className="ms-2">
                {context.t("I subscribe to project updates")}
              </span>
            </Label>
          )}
        </>
      )}

      <div className="text-center">
        <Button
          color="primary"
          className="w-50"
          disabled={watch("userSharesEmail") && !isValid}
        >
          {context.t("Submit")}
        </Button>
      </div>
    </Form>
  );
};

/**  define proptype for the 'translation' function  */
BowlineConnectForm.contextTypes = {
  t: PropTypes.func,
};

BowlineConnectForm.propTypes = {};

export default BowlineConnectForm;
